import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { listMaterials } from "../api/materials";
import { importSalesOrders } from "../api/salesOrders";

const DIMENSION_COLUMN_LABELS = [
  "Gramaj",
  "Kağıt Cinsi",
  "Tabak Çapı",
  "Kalıp Tipi",
  "Renk",
  "Pantone Kodu",
  "Ambalaj Tipi",
  "Paket İçi Adet",
  "Ürün Etiket Sayısı",
  "Etiket Yeri",
  "İç Koli Adedi",
  "İç Koli Etiket Sayısı",
  "Dış Koli Adedi",
  "Dış Koli Etiket Sayısı",
];

const FIXED_COLUMN_LABELS = {
  order_no: "Sipariş No",
  customer_name: "Müşteri Adı",
  customer_product_code: "Müşteri Ürün Kodu",
  material_code: "Ürün Kodu",
  product_name: "Ürün Adı",
  note: "Ürün Tanımı",
  manufacturing_source: "Üretim Kaynağı",
  injet_no: "Injet",
  qty: "Miktar",
  due_date: "Termin Tarihi",
  po_date: "PO TARIHI",
  pi_date: "PI TARIHI",
};

// PO/PI tarihleri opsiyoneldir; Excel'de sütun yoksa içe aktarma engellenmez.
const OPTIONAL_FIXED_COLUMNS = new Set(["customer_product_code", "product_name", "note", "injet_no", "po_date", "pi_date"]);

function normalizeHeaderKey(x) {
  return String(x || "")
    .trim()
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    // "PO TARİHİ" ve "PO TARIHI" tr-locale küçültmede farklı sonuç verir (i/ı).
    // Noktalı/noktasız i ayrımını sil ki başlık eşleşmesi ikisini de yakalasın.
    .replace(/[ıi̇]/g, "i");
}

function toYmd(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  if (!s) return null;
  // DD.MM.YYYY or DD/MM/YYYY → YYYY-MM-DD
  const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (m) {
    const d = m[1].padStart(2, "0");
    const mo = m[2].padStart(2, "0");
    return `${m[3]}-${mo}-${d}`;
  }
  return s;
}

function toInt(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(",", "."));
  if (!Number.isFinite(n)) return null;
  if (!Number.isInteger(n)) return null;
  return n;
}

function validateRow(r) {
  const errors = [];
  if (!String(r.order_no || "").trim()) errors.push("Sipariş No boş");
  if (!String(r.customer_name || "").trim()) errors.push("Müşteri Adı boş");
  if (!String(r.material_code || "").trim()) errors.push("Ürün Kodu boş");
  if (!r.material_id) errors.push("Ürün sistemde bulunamadı");
  if (!String(r.due_date || "").trim()) errors.push("Termin Tarihi boş");
  if (!Number.isInteger(Number(r.qty)) || Number(r.qty) <= 0) errors.push("Miktar geçersiz");
  return errors;
}

export default function SalesOrdersImportModal({ onClose, onImported }) {
  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [rows, setRows] = useState([]);

  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const materialByCode = useMemo(() => {
    const m = new Map();
    for (const it of materials || []) {
      const code = String(it.code || "").trim().toLowerCase();
      if (code) m.set(code, it);
    }
    return m;
  }, [materials]);

  const validated = useMemo(() => {
    const withErrors = (rows || []).map((r) => ({ ...r, _errors: validateRow(r) }));
    const errorCount = withErrors.reduce((acc, r) => acc + (r._errors?.length || 0), 0);
    const hasBlocking = withErrors.some((r) => (r._errors?.length || 0) > 0);
    return { rows: withErrors, errorCount, hasBlocking };
  }, [rows]);

  useEffect(() => {
    setLoadingMaterials(true);
    listMaterials({ limit: 500 })
      .then((items) => setMaterials(items || []))
      .catch((err) => {
        console.error("MATERIALS LOAD ERROR", err);
      })
      .finally(() => setLoadingMaterials(false));
  }, []);

  // Re-match material_id when materials arrive after rows were parsed
  useEffect(() => {
    if (!materialByCode.size || !rows.length) return;
    setRows((prev) =>
      prev.map((r) => {
        if (r.material_id) return r;
        const code = String(r.material_code || "").trim().toLowerCase();
        const matched = code ? materialByCode.get(code) : null;
        if (!matched) return r;
        return { ...r, material_id: Number(matched.id), material_name: String(matched.name || "") };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialByCode]);

  async function handlePickFile(file) {
    setParseError("");
    setSubmitError("");
    setResult(null);
    setRows([]);
    setFileName(file?.name || "");
    if (!file) return;

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) throw new Error("Excel sayfası bulunamadı");
      const ws = wb.Sheets[sheetName];

      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", blankrows: false });
      if (!Array.isArray(aoa) || aoa.length < 2) throw new Error("Excel boş veya başlık satırı eksik");

      // Find first row that has at least one non-empty cell (handles sheets starting from B1 etc.)
      let headerRowIndex = 0;
      for (let i = 0; i < Math.min(aoa.length, 5); i++) {
        if ((aoa[i] || []).some((c) => String(c || "").trim())) { headerRowIndex = i; break; }
      }
      const headerRow = aoa[headerRowIndex] || [];
      const dataRows = aoa.slice(headerRowIndex + 1);
      const headers = headerRow.map((h) => normalizeHeaderKey(h));
      const originalHeaders = headerRow.map((h) => String(h || "").trim());

      const findIdx = (label) => headers.indexOf(normalizeHeaderKey(label));

      const fixedIdx = {};
      for (const [key, label] of Object.entries(FIXED_COLUMN_LABELS)) {
        fixedIdx[key] = findIdx(label);
      }

      const dimensionIdx = {};
      for (const label of DIMENSION_COLUMN_LABELS) {
        const i = findIdx(label);
        if (i >= 0) dimensionIdx[label] = i;
      }

      const parsed = [];
      for (let i = 0; i < dataRows.length; i += 1) {
        const r = dataRows[i] || [];
        const orderNo = fixedIdx.order_no >= 0 ? String(r[fixedIdx.order_no] || "").trim() : "";
        const customerName =
          fixedIdx.customer_name >= 0 ? String(r[fixedIdx.customer_name] || "").trim() : "";
        const customerProductCode =
          fixedIdx.customer_product_code >= 0 ? String(r[fixedIdx.customer_product_code] || "").trim() : "";
        const materialCode =
          fixedIdx.material_code >= 0 ? String(r[fixedIdx.material_code] || "").trim() : "";

        const isEmpty = !orderNo && !customerName && !materialCode;
        if (isEmpty) continue;

        const dims = {};
        for (const [label, idx] of Object.entries(dimensionIdx)) {
          const raw = r[idx];
          if (raw === null || raw === undefined) continue;
          const val = String(raw).trim();
          if (val) dims[label] = val;
        }

        const row = {
          order_no: orderNo,
          customer_name: customerName,
          customer_product_code: customerProductCode,
          material_code: materialCode,
          product_name: fixedIdx.product_name >= 0 ? String(r[fixedIdx.product_name] || "").trim() : "",
          note: fixedIdx.note >= 0 ? String(r[fixedIdx.note] || "").trim() : "",
          manufacturing_source:
            fixedIdx.manufacturing_source >= 0
              ? String(r[fixedIdx.manufacturing_source] || "").trim()
              : "",
          injet_no: fixedIdx.injet_no >= 0 ? String(r[fixedIdx.injet_no] || "").trim() : "",
          qty: fixedIdx.qty >= 0 ? toInt(r[fixedIdx.qty]) : null,
          due_date: fixedIdx.due_date >= 0 ? toYmd(r[fixedIdx.due_date]) : null,
          po_date: fixedIdx.po_date >= 0 ? toYmd(r[fixedIdx.po_date]) : null,
          pi_date: fixedIdx.pi_date >= 0 ? toYmd(r[fixedIdx.pi_date]) : null,
          variant_dimensions: dims,
        };

        const matched = row.material_code
          ? materialByCode.get(row.material_code.toLowerCase())
          : null;
        row.material_id = matched ? Number(matched.id) : null;
        row.material_name = matched ? String(matched.name || "") : "";

        parsed.push(row);
      }

      setRows(parsed);
      const missingFixed = Object.entries(FIXED_COLUMN_LABELS)
        .filter(([k]) => fixedIdx[k] < 0 && !OPTIONAL_FIXED_COLUMNS.has(k))
        .map(([, label]) => label);
      if (missingFixed.length) {
        setParseError(`Eksik sütun(lar): ${missingFixed.join(", ")}`);
      } else if (!parsed.length) {
        setParseError("Dosyada satır bulunamadı (başlık hariç).");
      }

      // Debug: show unrecognized columns
      const recognized = new Set([
        ...Object.values(FIXED_COLUMN_LABELS).map(normalizeHeaderKey),
        ...DIMENSION_COLUMN_LABELS.map(normalizeHeaderKey),
      ]);
      const unknown = originalHeaders.filter(
        (h) => h && !recognized.has(normalizeHeaderKey(h))
      );
      if (unknown.length) {
        console.warn("UNKNOWN EXCEL COLUMNS", unknown);
      }
    } catch (err) {
      console.error("EXCEL PARSE ERROR", err);
      setParseError(err?.message || "Excel okunamadı");
    }
  }

  async function submit() {
    setSubmitError("");
    setResult(null);

    if (!rows.length) return;
    if (validated.hasBlocking) {
      setSubmitError("Hatalı satırlar var. Lütfen düzeltin.");
      return;
    }

    setSaving(true);
    try {
      const payloadRows = rows.map((r) => ({
        order_no: r.order_no,
        customer_name: r.customer_name,
        customer_product_code: r.customer_product_code || null,
        material_code: r.material_code,
        product_name: r.product_name || null,
        note: r.note || null,
        manufacturing_source: r.manufacturing_source || null,
        injet_no: r.injet_no || null,
        qty: Number(r.qty),
        due_date: r.due_date || null,
        po_date: r.po_date || null,
        pi_date: r.pi_date || null,
        variant_dimensions: r.variant_dimensions || {},
      }));

      const res = await importSalesOrders({ rows: payloadRows });
      setResult(res);
      await onImported?.(res);
    } catch (err) {
      console.error("SALES ORDER IMPORT ERROR", err);
      const status = err?.response?.status;
      const dataObj = err?.response?.data;
      const message =
        dataObj?.error?.message ||
        dataObj?.message ||
        (typeof dataObj === "string" ? dataObj : null) ||
        err?.message ||
        null;
      setSubmitError(`İçe aktarılamadı${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Excel İçe Aktar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Dosyayı seçin; satış siparişleri ve kalemleri otomatik oluşturulur.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {parseError ? <Alert severity="error">{parseError}</Alert> : null}
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}

          {result ? (
            <Alert severity={result?.errors?.length ? "warning" : "success"}>
              Oluşturulan sipariş: <b>{result?.created?.sales_orders || 0}</b>, kalem:{" "}
              <b>{result?.created?.items || 0}</b>
              {result?.errors?.length ? (
                <>
                  <br />
                  Hatalı sipariş grubu: <b>{result.errors.length}</b>
                  <ul style={{ margin: "4px 0 0 16px" }}>
                    {result.errors.map((e, i) => (
                      <li key={i}>
                        <b>{e.order_no}</b>: {e.message}
                        {Array.isArray(e.details?.warnings) && e.details.warnings.length ? (
                          <ul style={{ margin: "2px 0 0 16px" }}>
                            {e.details.warnings.map((w, j) => (
                              <li key={j}>{w}</li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Button variant="outlined" component="label" disabled={saving}>
              Dosya Seç (.xlsx)
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={(e) => handlePickFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {fileName ? `Seçili: ${fileName}` : "Henüz dosya seçilmedi"}
              {loadingMaterials ? " (malzemeler yükleniyor...)" : ""}
            </Typography>
          </Stack>

          {validated.rows.length ? (
            <Box sx={{ overflowX: "auto" }}>
              <Typography variant="body2" color={validated.hasBlocking ? "error" : "text.secondary"}>
                Satır: <b>{validated.rows.length}</b> — Hata: <b>{validated.errorCount}</b>
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Sipariş No</TableCell>
                    <TableCell>Müşteri</TableCell>
                    <TableCell>Müşteri Ürün Kodu</TableCell>
                    <TableCell>Ürün Kodu</TableCell>
                    <TableCell>Ürün Adı</TableCell>
                    <TableCell>Miktar</TableCell>
                    <TableCell>Termin</TableCell>
                    <TableCell>PO Tarihi</TableCell>
                    <TableCell>PI Tarihi</TableCell>
                    <TableCell>Kaynak</TableCell>
                    <TableCell>Injet</TableCell>
                    <TableCell>Varyant</TableCell>
                    <TableCell>Hata</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validated.rows.map((r, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{r.order_no || ""}</TableCell>
                      <TableCell>{r.customer_name || ""}</TableCell>
                      <TableCell>{r.customer_product_code || ""}</TableCell>
                      <TableCell>{r.material_code || ""}</TableCell>
                      <TableCell>{r.material_name || r.product_name || ""}</TableCell>
                      <TableCell>{r.qty ?? ""}</TableCell>
                      <TableCell>{r.due_date || ""}</TableCell>
                      <TableCell>{r.po_date || ""}</TableCell>
                      <TableCell>{r.pi_date || ""}</TableCell>
                      <TableCell>{r.manufacturing_source || ""}</TableCell>
                      <TableCell>{r.injet_no || ""}</TableCell>
                      <TableCell sx={{ maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {Object.entries(r.variant_dimensions || {})
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(" | ")}
                      </TableCell>
                      <TableCell>
                        {r._errors?.length ? (
                          <Typography variant="caption" color="error">
                            {r._errors.join(", ")}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="success.main">
                            OK
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Kapat
        </Button>
        <Button onClick={submit} variant="contained" disabled={saving || !rows.length || validated.hasBlocking}>
          {saving ? "Oluşturuluyor..." : "Oluştur"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
